import React, { forwardRef } from 'react'
import ReactButton from 'react-bootstrap/Button'
import PropTypes from 'prop-types'
import '../../globals/scss/components/button.scss'
import { concatClasses } from '../../utils/fns'

const propTypes = {
  /**
   * @default 'btn'
   */
  bsPrefix: PropTypes.string,

  className: PropTypes.string,

  /**
   * One or more button variant combinations
   *
   * buttons may be one of a variety of visual variants such as:
   *
   * `'-shadow', '-text', 'success', 'danger', 'warning', 'info', 'dark', 'light', 'link'`
   *
   * as well as "outline" versions (prefixed by 'outline-*')
   *
   * `'outline-primary', 'outline-secondary', 'outline-success', 'outline-danger', 'outline-warning', 'outline-info', 'outline-dark', 'outline-light'`
   */
  variant: PropTypes.string,

  /**
   * Specifies a large or small button.
   *
   * @type ('sm'|'lg')
   */
  size: PropTypes.string,

  /** Spans the full width of the Button parent */
  block: PropTypes.bool,

  /** Manually set the visual state of the button to `:active` */
  active: PropTypes.bool,

  /**
   * Disables the Button, preventing mouse events,
   * even if the underlying component is an `<a>` element
   */
  disabled: PropTypes.bool,

  /** Providing a `href` will render an `<a>` element, _styled_ as a button. */
  href: PropTypes.string,

  /**
   * Defines HTML button type attribute.
   *
   * @default 'button'
   */
  type: PropTypes.oneOf(['button', 'reset', 'submit', null]),

  as: PropTypes.elementType,

  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
}

const defaultProps = {
  variant: 'basic',
  active: false,
  disabled: false,
  type: 'button',
  bsPrefix: ' ',
}

export const Button = forwardRef(({ children, className, ...props }, ref) => (
  <ReactButton ref={ref} className={concatClasses('button', className)} {...props}>
    {children}
  </ReactButton>
))

Button.displayName = 'Button'

Button.propTypes = propTypes
Button.defaultProps = defaultProps
